/**
 * 71.2 (DECISIONS 324, ARCHITECTURE §5.36 (3)): the ground a thing stands on — a rock, a tree, a felled stump, a fallen
 * limb — in the sim and the drawing alike. It stood on the coarse mesh (67.50 (1)), the fine relief rising round it: the
 * owner's "little depressions" everywhere, right in snow, wrong on rock and packed earth. Now it stands on the one ground's
 * `ground` level (the relief, digs, craterlets and terraces; not the buildings' decks — a rock is not on a deck, and a deck
 * laid or lost leaves it where it is) at its footprint's lowest point (the centre and four points round it at `half`, so no side floats), less a
 * sink by the ground's dry material there (constants/ground.ts PROP_SINK_PEOPLE: deep in snow, none on rock or ice). Where
 * there is no fine ground (classic, the relief's amplitude 0) it is the mesh, bit for bit (`getSurfaceRadius`).
 *
 * Kept per direction object (a prop's `surfaceDir`, a walk obstacle's `dir`) and planet object — an edit (a crater, a dig,
 * a craterlet) makes a new planet, so a thing is seated again the next time it is asked, after an edit and never per tick.
 */
import type { Craterlet, Dig, PlanetState, RockState, Terrace, Vec3 } from '../types';
import { PROP_SINK_PEOPLE } from '../constants/ground';
import { NORMAL_ROCK_PERSONS, NORMAL_ROCK_SIZE } from '../constants/action';
import { PERSON_BODY_HEIGHT } from './scaleProfile';
import { groundAt, type GroundDials } from './fineTerrain';
import { getSurfaceRadius } from './surface';
import { groundMaterialAt } from './groundMaterial';
import { simWork } from './workCounters';
import { DIG_REACH } from './dig';
import { CRATERLET_REACH } from './craterlets';
import { terraceReach } from './terraceGround';

interface SeatKept { planet: PlanetState; amp: number; wl: number; k: number; mr: boolean; half: number; r: number }
const seats = new WeakMap<Vec3, SeatKept>();

/**
 * The radius a thing at `dir` with a footprint `half` (world units along the ground; 0: a point, a tree's trunk) stands at
 * on `planet`: the ground's lowest point under it less its material's sink — the mesh where there is no fine ground.
 */
export function seatGround(planet: PlanetState, dir: Vec3, ps: GroundDials, half = 0): number {
  if (!(ps.fineAmplitude > 0)) return getSurfaceRadius(planet, dir);
  const m = seats.get(dir);
  if (m && m.amp === ps.fineAmplitude && m.wl === ps.fineWavelength && m.k === ps.k && m.mr === ps.materialRelief && m.half === half) {
    if (m.planet === planet) return m.r;
    // (a new planet object on the same mesh — a craterlet or a dig elsewhere: kept unless an edit came or went within reach)
    const was = m.planet;
    if (was.heightmap === planet.heightmap && was.basePositions === planet.basePositions && was.radius === planet.radius
      && !nearEdits(editsChanged(fineEditsOf(was), planet, ps.k), planet, dir, half)) { m.planet = planet; return m.r; }
  }
  simWork.propSeats++;
  const l = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  const ux = dir.x / l, uy = dir.y / l, uz = dir.z / l, u = { x: ux, y: uy, z: uz };
  let low = groundAt(planet, u, 'ground', ps);
  if (half > 0) {
    // two tangent axes at u (any pair: the footprint is a disc), the four points `half` out along them
    // (u × the reference axis: y, or x where u is near y)
    const ry = Math.abs(uy) < 0.9 ? 1 : 0, rx = 1 - ry;
    let ax = -uz * ry, ay = uz * rx, az = ux * ry - uy * rx;
    const al = Math.sqrt(ax * ax + ay * ay + az * az);
    ax /= al; ay /= al; az /= al;
    const bx = uy * az - uz * ay, by = uz * ax - ux * az, bz = ux * ay - uy * ax;
    const t = half / planet.radius, p = { x: 0, y: 0, z: 0 };
    for (let q = 0; q < 4; q++) {
      const sa = q === 0 ? t : q === 1 ? -t : 0, sb = q === 2 ? t : q === 3 ? -t : 0;
      p.x = ux + ax * sa + bx * sb; p.y = uy + ay * sa + by * sb; p.z = uz + az * sa + bz * sb;
      const g = groundAt(planet, p, 'ground', ps);
      if (g < low) low = g;
    }
  }
  const r = low - sinkAt(planet, u, ps);
  if (m) { m.planet = planet; m.amp = ps.fineAmplitude; m.wl = ps.fineWavelength; m.k = ps.k; m.mr = ps.materialRelief; m.half = half; m.r = r; }
  else seats.set(dir, { planet, amp: ps.fineAmplitude, wl: ps.fineWavelength, k: ps.k, mr: ps.materialRelief, half, r });
  return r;
}

/** How far a thing sinks into the ground's dry material at `dir` (world units; constants/ground.ts PROP_SINK_PEOPLE — deep in snow, none on rock or ice). */
export function sinkAt(planet: PlanetState, dir: Vec3, ps: Pick<GroundDials, 'k'>): number {
  return PROP_SINK_PEOPLE[groundMaterialAt(planet, dir)] * PERSON_BODY_HEIGHT * ps.k;
}

/** A rock's footprint for its seat (world units): a normal rock's walk disc (its half × its scale, person-sized); 0 for a planet-scale one (its centre). */
export function rockSeatHalf(r: Pick<RockState, 'size' | 'rockType' | 'scale'>, ps: Pick<GroundDials, 'k'>): number {
  const n = r.size === 'normal' ? NORMAL_ROCK_SIZE[r.rockType] : undefined;
  return n ? n.half * r.scale * NORMAL_ROCK_PERSONS * (PERSON_BODY_HEIGHT * ps.k) : 0;   // (the walk's own product, term for term)
}

/**
 * A thing's ground radius as every reader takes it — the walk's, the shapes', a crown's, a fallen limb's, the drawing's:
 * on the fine ground by its `seat` (seatGround), and where there is no fine ground (no dials, or classic's amplitude 0) the
 * mesh at `up` exactly as before (`getSurfaceRadius(planet, up)`: the turn game's numbers, bit for bit).
 */
export function seatRadius(planet: PlanetState, seat: { dir: Vec3; half: number } | undefined, up: Vec3, ps: GroundDials | null | undefined): number {
  return seat && ps && ps.fineAmplitude > 0 ? seatGround(planet, seat.dir, ps, seat.half) : getSurfaceRadius(planet, up);
}

/**
 * The fine ground's edits a seat stands on — the digs, the craterlets, the terraces (the heightmap aside, which the
 * readers already follow face by face). Kept beside a cache of seats (the shapes, the crowns), so an edit re-seats only
 * what stands within its reach (`editsChanged`, `nearEdits`), as a crater re-makes only the shapes over moved ground.
 */
export type FineEdits = readonly [Dig[] | undefined, Craterlet[] | undefined, Terrace[] | undefined];
export const fineEditsOf = (p: PlanetState): FineEdits => [p.digs, p.craterlets, p.terraces];
export const sameEdits = (e: FineEdits, p: PlanetState): boolean => e[0] === p.digs && e[1] === p.craterlets && e[2] === p.terraces;

/** An edit that came or went, as its centre (a unit direction) and how far it reaches (world units). */
export interface EditReach { dir: Vec3; reach: number }

let lastChanged: { before: FineEdits; after: FineEdits; k: number; out: EditReach[] } | null = null;
/**
 * The edits in `before` or in `planet` but not both (a record is replaced, never changed in place: a deepened craterlet
 * is a new record), each with its reach — empty when the edits are the same. Kept for the last pair asked.
 */
export function editsChanged(before: FineEdits, planet: PlanetState, k: number): readonly EditReach[] {
  const after = fineEditsOf(planet);
  if (before[0] === after[0] && before[1] === after[1] && before[2] === after[2]) return [];
  const c = lastChanged;
  if (c && c.k === k && c.before[0] === before[0] && c.before[1] === before[1] && c.before[2] === before[2] && c.after[0] === after[0] && c.after[1] === after[1] && c.after[2] === after[2]) return c.out;
  const out: EditReach[] = [], person = PERSON_BODY_HEIGHT * k;
  const diff = <T extends { dir: Vec3 }>(a: readonly T[] | undefined, b: readonly T[] | undefined, reach: (x: T) => number) => {
    if (a === b) return;
    const inA = new Set(a ?? []), inB = new Set(b ?? []);
    for (const x of a ?? []) if (!inB.has(x)) out.push({ dir: x.dir, reach: reach(x) });
    for (const x of b ?? []) if (!inA.has(x)) out.push({ dir: x.dir, reach: reach(x) });
  };
  diff(before[0], after[0], () => (DIG_REACH * person) / 1.8);   // (a dig's reach is in person units, 1.8 to a person)
  diff(before[1], after[1], () => CRATERLET_REACH * person);
  diff(before[2], after[2], t => terraceReach(t));
  lastChanged = { before, after, k, out };
  return out;
}

/** Does a thing at `dir` with a footprint `half` stand within any of `changed`'s reach (its seat may have moved)? */
export function nearEdits(changed: readonly EditReach[], planet: PlanetState, dir: Vec3, half: number): boolean {
  if (changed.length === 0) return false;
  const l = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  for (const c of changed) {
    const cl = Math.sqrt(c.dir.x * c.dir.x + c.dir.y * c.dir.y + c.dir.z * c.dir.z);
    const cos = (dir.x * c.dir.x + dir.y * c.dir.y + dir.z * c.dir.z) / (l * cl);
    // (a chord's length against the reach along the ground: the chord is the shorter, so this keeps a hair more)
    if (2 * planet.radius * Math.sqrt(Math.max(0, (1 - cos) / 2)) <= c.reach + half) return true;
  }
  return false;
}
